import { useState, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Badge, Button, Card, Input, Table } from '@/admin-design/_common';
import { api } from '../api/client';

type Mode = 'margin' | 'pct' | 'flat' | 'expose';
type Scope = 'all' | 'margin_only' | 'product_ids';

type PreviewItem = {
  product_id: number;
  name: string;
  cost_price: number;
  expose_price: number;
  current_price: number;
  price_source: string;
  new_price: number;
  skip_reason?: string;
};

export default function BulkAdjustPage() {
  const [params] = useSearchParams();
  const initialIds = useMemo(() => {
    const v = params.get('product_ids');
    if (!v) return [] as number[];
    return v.split(',').map((s) => Number(s)).filter((n) => Number.isFinite(n) && n > 0);
  }, [params]);

  const [mode, setMode] = useState<Mode>('margin');
  // value 는 raw 문자열로 관리 (사용자가 "-" 만 친 partial 입력도 보존되도록).
  // API 호출 시점에만 number 로 파싱.
  const [valueText, setValueText] = useState<string>('1.5');
  const value = (() => {
    const n = parseFloat(valueText);
    return Number.isFinite(n) ? n : 0;
  })();
  const [scope, setScope] = useState<Scope>(initialIds.length > 0 ? 'product_ids' : 'margin_only');
  const productIds = initialIds;
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const preview = useMutation({
    mutationFn: () => api.bulkAdjustPreview({ mode, value, scope, product_ids: productIds }),
  });

  const execute = useMutation({
    mutationFn: () =>
      api.createJob('price_bulk_adjust', {
        mode,
        value,
        scope,
        product_ids: productIds,
        dry_run: false,
      }),
    onSuccess: () => {
      setMsg({ ok: true, text: '잡이 큐에 등록되었습니다. /jobs 에서 진행 확인하세요.' });
    },
    onError: (err: any) => {
      setMsg({ ok: false, text: err?.message ?? '큐 등록 실패' });
    },
  });

  const items: PreviewItem[] = preview.data?.items ?? [];
  const applyCount: number = preview.data?.apply_count ?? 0;
  const skipCount: number = preview.data?.skip_count ?? 0;
  const total: number = preview.data?.total ?? 0;

  const onPreview = () => preview.mutate();
  const onExecute = () => {
    if (!confirm(`적용 ${applyCount}건 / 스킵 ${skipCount}건. 정말 실행할까요?`)) return;
    execute.mutate();
  };

  const valueHelp =
    mode === 'margin'
      ? '예: 1.5 = 도매가의 150% (50% 마진)'
      : mode === 'pct'
      ? '예: 0.97 = 현재가의 97% (-3% 인하)'
      : mode === 'flat'
      ? '예: -1000 = 1000원 차감'
      : '값 무시 — 각 상품의 도매최저노출가를 그대로 적용합니다.';

  return (
    <div className="space-y-4 max-w-4xl">
      <h1 className="text-2xl font-bold">일괄 가격 조정</h1>

      <Card className="shadow-sm">
        <Card.Header>
          <Card.Title>조정 규칙</Card.Title>
          <Card.Description>
            모드 + 값 + 적용 범위를 지정하고 미리보기 후 실행합니다.
          </Card.Description>
        </Card.Header>
        <div className="px-6 py-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-[minmax(160px,240px)_1fr] gap-x-6 gap-y-1">
            <label className="text-sm font-medium text-foreground md:pt-2">
              모드
              <p className="text-xs text-muted-foreground font-normal mt-0.5">
                margin: 도매가×값, pct: 현재가×값, flat: 현재가+값
              </p>
            </label>
            <div>
              <select
                className="border border-border rounded px-2 py-1 bg-card text-foreground text-sm"
                value={mode}
                onChange={(e) => setMode(e.target.value as Mode)}
              >
                <option value="margin">margin (도매가 마진율)</option>
                <option value="pct">pct (정률 조정)</option>
                <option value="flat">flat (정액 가감)</option>
                <option value="expose">expose (도매최저노출가)</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[minmax(160px,240px)_1fr] gap-x-6 gap-y-1">
            <label className="text-sm font-medium text-foreground md:pt-2">
              값
              <p className="text-xs text-muted-foreground font-normal mt-0.5">{valueHelp}</p>
            </label>
            <div>
              <Input
                type="number"
                className="w-40 font-mono"
                step={mode === 'flat' ? '100' : '0.01'}
                value={valueText}
                onChange={(e) => setValueText(e.target.value)}
                disabled={mode === 'expose'}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[minmax(160px,240px)_1fr] gap-x-6 gap-y-1">
            <label className="text-sm font-medium text-foreground md:pt-2">
              적용 범위
              {scope === 'product_ids' && productIds.length > 0 && (
                <p className="text-xs text-muted-foreground font-normal mt-0.5">
                  {productIds.length}개 선택됨 (Products 페이지에서 전달)
                </p>
              )}
            </label>
            <div>
              <select
                className="border border-border rounded px-2 py-1 bg-card text-foreground text-sm"
                value={scope}
                onChange={(e) => setScope(e.target.value as Scope)}
              >
                <option value="all">전체 synced 리스팅</option>
                <option value="margin_only">price_source = margin 만</option>
                <option value="product_ids" disabled={productIds.length === 0}>
                  선택 상품 ({productIds.length}건)
                </option>
              </select>
            </div>
          </div>
        </div>
        <Card.Footer className="flex items-center justify-between gap-3">
          <div className="text-xs">
            {msg && (
              <span className={msg.ok ? 'text-success' : 'text-destructive'}>{msg.text}</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button onClick={onPreview} loading={preview.isPending} variant="secondary" size="sm">
              미리보기
            </Button>
            <Button
              onClick={onExecute}
              loading={execute.isPending}
              disabled={execute.isPending || !preview.data || applyCount === 0}
              variant="primary"
              size="sm"
            >
              실행 ({applyCount}건 적용)
            </Button>
          </div>
        </Card.Footer>
      </Card>

      {preview.data && (
        <Card className="shadow-sm">
          <Card.Header>
            <Card.Title>미리보기</Card.Title>
            <Card.Description>
              총 {total}건 — 적용 {applyCount} / 스킵 {skipCount}
            </Card.Description>
          </Card.Header>
          <div className="overflow-x-auto">
            <Table>
              <Table.Head>
                <Table.Row>
                  <Table.Header>ID</Table.Header>
                  <Table.Header>상품명</Table.Header>
                  <Table.Header className="text-right">도매가</Table.Header>
                  <Table.Header className="text-right">노출가</Table.Header>
                  <Table.Header className="text-right">현재가</Table.Header>
                  <Table.Header>price_source</Table.Header>
                  <Table.Header className="text-right">신가</Table.Header>
                  <Table.Header>판정</Table.Header>
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {items.map((it) => (
                  <Table.Row key={it.product_id}>
                    <Table.Cell>
                      <Link
                        to={`/products/${it.product_id}`}
                        className="text-primary hover:underline"
                      >
                        {it.product_id}
                      </Link>
                    </Table.Cell>
                    <Table.Cell className="max-w-md truncate" title={it.name}>
                      {it.name}
                    </Table.Cell>
                    <Table.Cell className="text-right font-mono">
                      {it.cost_price.toLocaleString()}
                    </Table.Cell>
                    <Table.Cell className="text-right font-mono">
                      {it.expose_price > 0 ? it.expose_price.toLocaleString() : '—'}
                    </Table.Cell>
                    <Table.Cell className="text-right font-mono">
                      {it.current_price.toLocaleString()}
                    </Table.Cell>
                    <Table.Cell className="text-xs">{it.price_source}</Table.Cell>
                    <Table.Cell className="text-right font-mono">
                      {it.new_price > 0 ? it.new_price.toLocaleString() : '—'}
                    </Table.Cell>
                    <Table.Cell>
                      {it.skip_reason ? (
                        <Badge variant="warning">{it.skip_reason}</Badge>
                      ) : (
                        <Badge variant="success">apply</Badge>
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}
